import { useState } from 'react';
import { AddToBasket, type AddToBasketType, type JavascriptWebsiteSettingsRevieve } from 'autogen/swagger/all';
import { closeAllDrawers } from 'components/generic/Drawer/hooks/useDrawer';
import type { RevieveActionTypes } from 'components/shared/Routing/components/CustomAction/shared/types';
import Toast from 'components/Toast';
import useRevieveSettings from 'hooks/user/useRevieveSettings';
import Routes from 'modules/routes';
import { GA4Tracking } from 'modules/tracking/GA4';
import { scriptId, scriptSrc } from './constants';
import type { RevieveProduct } from './types';

type useOpenRevievePluginProps = {
    type: RevieveActionTypes;
    onClose?: () => void;
};

type InitRevieveConfigProps = useOpenRevievePluginProps & {
    revieveSettings: JavascriptWebsiteSettingsRevieve;
};

type BaseRevieveConfigProps = {
    env: string;
    locale: string;
    disableLauncherButton: boolean;
    avoidCookies: boolean;
    analytics: {
        disableUsageAnalytics: boolean;
        disableProductAnalytics: boolean;
        disableErrorReporting: boolean;
    };
    onClose: useOpenRevievePluginProps['onClose'];
    onAddToCart?: (revieveProduct: RevieveProduct) => void;
    onCheckout?: () => void;
    onClickProduct?: (revieveProduct: RevieveProduct) => void;
};

function openRevieveModal() {
    window.Revieve.API.show();
}

function trackOpenScript(type: RevieveActionTypes) {
    GA4Tracking({
        eventName: 'open_modal',
        context: 'revieve',
        category: 'advisory',
        eventParams: [{ param_name: 'revieve_action_type', param_value: type }]
    });
}

const addToBasketTypeMapping: Record<RevieveActionTypes, AddToBasketType> = {
    SkinAnalytics: 'RevieveSkinAnalytics',
    InStoreSkinAnalytics: 'RevieveSkinAnalytics',
    MakeupAdvisor: 'RevieveMakeupAdvisor',
    InStoreMakeupAdvisor: 'RevieveMakeupAdvisor'
};

function addRevieveProductToBasket(revieveProduct: RevieveProduct, type: RevieveActionTypes) {
    const { id } = revieveProduct;
    const addToBasketType = addToBasketTypeMapping[type];

    AddToBasket({
        productId: Number(id),
        fromPowerstep: false,
        addToBasketType: addToBasketType
    }).then((response) => {
        if (response.success) {
            Toast.success(response.message);
        } else {
            window.location.href = new URL(Routes.Basket.BasketPage, window.location.origin).toString();
        }
    });
}

function goToBasket() {
    window.location.href = window.location.origin + Routes.Basket.BasketPage;
}

function onClickProduct(revieveProduct: RevieveProduct) {
    const { url } = revieveProduct;

    window.location.href = url;
}

function loadRevieveScript() {
    return new Promise((resolve, reject) => {
        const existingScript = document.getElementById(scriptId);

        if (!existingScript) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = scriptSrc;
            script.async = true;
            script.onload = () => resolve(script);
            script.onerror = () => reject(new Error('Failed to load Revieve script'));
            const firstScript = document.getElementsByTagName('script')[0];
            firstScript.parentNode?.insertBefore(script, firstScript);
        } else {
            resolve(existingScript);
        }
    });
}

function mapRevieveConfig({ type, revieveSettings, onClose }: InitRevieveConfigProps) {
    const {
        makeupAdvisorPartnerId,
        skinAnalyticsPartnerId,
        inStoreMakeupAdvisorPartnerId,
        inStoreSkinAnalyticsPartnerId,
        env,
        locale
    } = revieveSettings;

    const baseConfig: BaseRevieveConfigProps = {
        env,
        locale,
        disableLauncherButton: true,
        avoidCookies: true,
        analytics: {
            disableUsageAnalytics: true,
            disableProductAnalytics: true,
            disableErrorReporting: true
        },
        onClose,
        onAddToCart: (revieveProduct) => addRevieveProductToBasket(revieveProduct, type),
        onCheckout: () => goToBasket(),
        onClickProduct: (revieveProduct) => onClickProduct(revieveProduct)
    };

    const partnerId = (() => {
        switch (type) {
            case 'InStoreMakeupAdvisor':
                return inStoreMakeupAdvisorPartnerId;
            case 'InStoreSkinAnalytics':
                return inStoreSkinAnalyticsPartnerId;
            case 'MakeupAdvisor':
                return makeupAdvisorPartnerId;
            case 'SkinAnalytics':
                return skinAnalyticsPartnerId;
            default:
                return '';
        }
    })();

    return { partner_id: partnerId, ...baseConfig };
}

function initRevieveConfig({ type, revieveSettings, onClose }: InitRevieveConfigProps) {
    const mappedConfig = mapRevieveConfig({ type, revieveSettings, onClose });

    return new Promise<void>((resolve, reject) => {
        window.Revieve.API.unmount(); // clear config

        if (!window.Revieve._config()) {
            window.Revieve.Init(mappedConfig, () => {
                resolve();
            });
        } else {
            reject();
        }
    });
}

export default function useOpenRevievePlugin(): ({ type, onClose }: useOpenRevievePluginProps) => Promise<void> {
    const [isInitialized, setIsInitialized] = useState(false);
    const revieveSettings = useRevieveSettings();

    return async ({ type, onClose }: useOpenRevievePluginProps) => {
        try {
            if (!isInitialized) {
                await loadRevieveScript();
                setIsInitialized(true);
            }
            await initRevieveConfig({ type, revieveSettings, onClose });

            openRevieveModal();
            closeAllDrawers();
            trackOpenScript(type);
        } catch (err) {
            throw err;
        }
    };
}
